import { apiClient } from './api';

export type InterlockLayer = 'firmware' | 'backend';
export type InterlockSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface ParameterFieldSchema {
  type: 'number' | 'boolean' | 'string' | 'select';
  label_cs: string;
  label_en: string;
  label_de?: string;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string | number; label_cs: string; label_en: string }>;
  default: unknown;
}

export interface InterlockDefinition {
  key: string;
  layer: InterlockLayer;
  severity: InterlockSeverity;
  description_cs: string;
  description_en: string;
  description_de: string | null;
  enabled: boolean;
  auto_resolves: boolean;
  blocks_actuators: number[] | null;
  overridable: boolean;
  parameters: Record<string, unknown>;
  parameters_schema: Record<string, ParameterFieldSchema>;
}

export interface UpdateDefinitionInput {
  enabled?: boolean;
  severity?: InterlockSeverity;
  auto_resolves?: boolean;
  overridable?: boolean;
  parameters?: Record<string, unknown>;
}

export interface InterlockEvent {
  id: number;
  device_id: string;
  interlock_key: string;
  layer: InterlockLayer;
  severity: InterlockSeverity;
  fired_at: string;
  resolved_at: string | null;
  reason: string;
  payload: Record<string, unknown>;
  override_id: number | null;
}

export interface InterlockOverride {
  id: number;
  device_id: string;
  interlock_keys: string[];
  created_by: string;
  reason: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
}

export interface CreateOverrideInput {
  deviceId: string;
  interlockKeys: string[];
  reason: string;
  durationMinutes: number;
}

export const interlockService = {
  getDefinitions: (): Promise<InterlockDefinition[]> =>
    apiClient.get<InterlockDefinition[]>('/interlocks/definitions'),

  getActive: (deviceId: string): Promise<InterlockEvent[]> =>
    apiClient.get<InterlockEvent[]>(`/interlocks/active?deviceId=${encodeURIComponent(deviceId)}`),

  getHistory: (deviceId: string, limit = 100, offset = 0): Promise<InterlockEvent[]> =>
    apiClient.get<InterlockEvent[]>(
      `/interlocks/history?deviceId=${encodeURIComponent(deviceId)}&limit=${limit}&offset=${offset}`,
    ),

  getOverrides: (deviceId: string): Promise<InterlockOverride[]> =>
    apiClient.get<InterlockOverride[]>(`/interlocks/overrides?deviceId=${encodeURIComponent(deviceId)}`),

  createOverride: (input: CreateOverrideInput): Promise<InterlockOverride> =>
    apiClient.post<InterlockOverride>('/interlocks/overrides', {
      deviceId: input.deviceId,
      interlockKeys: input.interlockKeys,
      reason: input.reason,
      durationMinutes: input.durationMinutes,
    }),

  revokeOverride: (id: number): Promise<InterlockOverride> =>
    apiClient.delete<InterlockOverride>(`/interlocks/overrides/${id}`),

  updateDefinition: (key: string, input: UpdateDefinitionInput): Promise<InterlockDefinition> =>
    apiClient.patch<InterlockDefinition>(`/interlocks/definitions/${encodeURIComponent(key)}`, input),
};
